import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import TestimonialCarousel from '../components/TestimonialCarousel';
import StudioPricingSection from '../components/StudioPricingSection';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { 
  ArrowRight, 
  CheckCircle, 
  Star, 
  Rocket, 
  Shield, 
  Palette, 
  Users, 
  Settings, 
  Target,
  Award,
  TrendingUp,
  Lightbulb,
  FileCheck,
  Building,
  ExternalLink,
  Play,
  Youtube
} from 'lucide-react';
import { markResourceDownloaded } from '../lib/leadMagnetSuppression';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_d8aebd02-1a2f-47f4-a1b5-34ff2bb8d378/artifacts/jr0zfrs3_David%20Ashley%20Workshop%20Pic.jpg";

function HeroLeadMagnetStrip() {
  const [form, setForm] = React.useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.email.trim()) {
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/resources/download`, {
        first_name: form.first_name.trim(),
        last_name: (form.last_name || form.first_name).trim(),
        email: form.email.trim().toLowerCase(),
        phone: (form.phone || '').trim(),
        resource_id: 'res_jumpstart_checklist',
        resource_name: '7-Stage Business Jumpstart Blueprint',
        lead_source: 'homepage_hero_strip',
        page_submitted_from: '/',
      });
      markResourceDownloaded('res_jumpstart_checklist', form.email);
      setDone(true);
    } catch (err) {
      // Best-effort; user can still grab the resource from /resources
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-gradient-to-r from-[#0F172A] to-[#1E293B]" data-testid="hero-leadmagnet-strip">
      <div className="container-custom py-10 lg:py-12">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-5 text-white">
            <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-[#F97316] mb-2">FREE Lead Magnet</div>
            <h3 className="font-bold text-2xl lg:text-3xl leading-tight mb-2">
              Get the <span className="text-[#F97316]">7-Stage Business Jumpstart Blueprint</span>
            </h3>
            <p className="text-[#CBD5E1] text-sm lg:text-base leading-relaxed max-w-md">
              A practical checklist to help you move from idea to structure, credibility, and launch. No fluff. Just the next steps.
            </p>
          </div>
          <div className="lg:col-span-7">
            {!done ? (
              <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="hero-leadmagnet-form">
                <input
                  required
                  type="text"
                  placeholder="First name"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  className="w-full px-4 py-3 bg-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#F97316]"
                  data-testid="hero-leadmagnet-first"
                />
                <input
                  type="text"
                  placeholder="Last name"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  className="w-full px-4 py-3 bg-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#F97316]"
                  data-testid="hero-leadmagnet-last"
                />
                <input
                  required
                  type="email"
                  placeholder="Email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 bg-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#F97316]"
                  data-testid="hero-leadmagnet-email"
                />
                <input
                  type="tel"
                  placeholder="Phone (optional)"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-4 py-3 bg-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#F97316]"
                  data-testid="hero-leadmagnet-phone"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="sm:col-span-2 bg-[#F97316] hover:bg-[#FB923C] text-white font-bold text-sm py-3 rounded-md transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="hero-leadmagnet-submit"
                >
                  {submitting ? 'Sending...' : (<>Send Me the Blueprint <ArrowRight size={16} /></>)}
                </button>
              </form>
            ) : (
              <div className="bg-emerald-500/10 border border-emerald-500/40 text-emerald-100 rounded-lg p-6 text-center" data-testid="hero-leadmagnet-success">
                <CheckCircle size={32} className="mx-auto text-emerald-400 mb-2" />
                <div className="font-bold text-lg">You are all set</div>
                <p className="text-sm text-[#CBD5E1] mt-1">The Blueprint is on its way to your inbox.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

const frameworkStages = [
  { number: '01', name: 'Foundation', description: 'Find your business idea, clarify vision, create action plan', icon: Lightbulb },
  { number: '02', name: 'Validation', description: 'Test demand, create starter offer, launch starter version', icon: Target },
  { number: '03', name: 'Credibility', description: 'Entity registration, EIN, bank account, business credit', icon: Shield },
  { number: '04', name: 'Brand', description: 'Logo, website, social channels, brand consistency', icon: Palette },
  { number: '05', name: 'Systems', description: 'CRM, accounting, pricing structure, marketing systems', icon: Settings },
  { number: '06', name: 'Protection', description: 'Insurance, contracts, legal review, data protection', icon: FileCheck },
  { number: '07', name: 'Launch', description: 'Sales funnel, email sequences, marketing, launch day', icon: Rocket },
];

const trustStats = [
  { value: '10+', label: 'Businesses Built' },
  { value: '7', label: 'Stage Framework' },
  { value: '100+', label: 'Entrepreneurs Helped' },
  { value: '15+', label: 'Years Experience' },
];

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [resources, setResources] = useState([]);
  const [brands, setBrands] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [ytData, setYtData] = useState(null);
  const settings = useSiteSettings();
  const hero = (settings && settings.hero) || {};
  const finalCta = (settings && settings.final_cta) || {};
  const videoSection = (settings && settings.video_section) || { enabled: false };
  const featuredIn = (settings && settings.featured_in) || { enabled: false, logos: [] };
  const brandExplainer = (settings && settings.brand_explainer) || 'These ventures are not distractions. They are real-world proof of the systems, strategy, and structure David teaches entrepreneurs every day.';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, resourcesRes, brandsRes, testimonialsRes] = await Promise.all([
          axios.get(`${API_URL}/api/services`),
          axios.get(`${API_URL}/api/resources`),
          axios.get(`${API_URL}/api/brands`),
          axios.get(`${API_URL}/api/testimonials`)
        ]);
        setServices(servicesRes.data.slice(0, 4));
        setResources(resourcesRes.data.slice(0, 2));
        setBrands(brandsRes.data);
        setTestimonials(testimonialsRes.data.filter(t => t.is_featured));
        try {
          const coachesRes = await axios.get(`${API_URL}/api/coaches/featured?limit=3`);
          setCoaches(coachesRes.data || []);
        } catch { /* coaches are optional on the homepage */ }
        try {
          const ytRes = await axios.get(`${API_URL}/api/public/youtube-latest?limit=6`);
          setYtData(ytRes.data || null);
        } catch { /* YouTube section is optional */ }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const getServiceIcon = (iconName) => {
    const icons = { rocket: Rocket, shield: Shield, palette: Palette, users: Users, settings: Settings, presentation: Award };
    return icons[iconName] || Target;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Ministry': 'bg-purple-100 text-purple-700',
      'Nonprofit': 'bg-green-100 text-green-700',
      'Business': 'bg-[#EFF6FF] text-[#1D4ED8]',
      'Health & Wellness': 'bg-emerald-100 text-emerald-700',
      'Career Development': 'bg-sky-100 text-sky-700',
      'Life & Purpose Coaching': 'bg-amber-100 text-amber-700',
      'Fresh Local Produce': 'bg-lime-100 text-lime-700',
      'Insurance Products': 'bg-rose-100 text-rose-700',
      'Digital Universe': 'bg-indigo-100 text-indigo-700'
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  return (
    <Layout
      title="DCA Enterprises Inc. - Turn Your Vision Into Additional Income Streams | David C. Ashley"
      description="Business strategist David C. Ashley helps visionaries, working professionals, and aspiring entrepreneurs structure their ideas, launch with confidence, and create additional income streams. Book your FREE 20-Minute Business Jumpstart Session."
    >
      {/* Hero Section */}
      <section className="relative bg-white overflow-hidden" data-testid="hero-section">
        {/* Ambient backdrop accents */}
        <div className="glow-blob blue" style={{ top: '-180px', right: '-160px' }} aria-hidden="true"></div>
        <div className="hidden lg:block absolute inset-y-0 left-0 w-1/2 dot-grid opacity-60 pointer-events-none" aria-hidden="true"></div>
        <div className="container-custom relative">
          <div className="grid lg:grid-cols-12 gap-8 items-center py-10 lg:py-14">
            {/* Left Content */}
            <div className="animate-fade-in-up lg:col-span-7">
              <span className="overline mb-4 inline-block" data-testid="hero-overline">{hero.overline || 'Business Strategist'}</span>
              <h1 className="heading-1 text-4xl sm:text-5xl lg:text-6xl text-[#0A0F1C] mb-6" data-testid="hero-title">
                {hero.headline_a || 'Turn Your Vision Into'}{' '}
                <span className="text-[#1D4ED8]">{hero.headline_b || 'Additional Income'}</span>{' '}
                <span className="text-[#F97316]">{hero.headline_c || 'Streams'}</span>
              </h1>
              <p className="body-text text-[#475569] text-lg mb-8 max-w-xl" data-testid="hero-description">
                {hero.sub || 'I help visionaries, aspiring entrepreneurs, struggling business owners, and working professionals structure their ideas, launch with confidence, and create additional income streams outside of their 9-to-5.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to={hero.primary_cta_link || '/free-jumpstart-session'}
                  className="btn-primary-orange inline-flex items-center justify-center gap-2"
                  data-testid="hero-cta-primary"
                >
                  {hero.primary_cta_label || 'Book Your FREE 20-Minute Business Jumpstart Session'}
                  <ArrowRight size={18} />
                </Link>
                <Link
                  to={hero.secondary_cta_link || '/resources'}
                  className="btn-outline inline-flex items-center justify-center gap-2"
                  data-testid="hero-cta-secondary"
                >
                  {hero.secondary_cta_label || 'Get Free Business Resources'}
                </Link>
              </div>
            </div>

            {/* Right Image - asymmetric / overlapping treatment */}
            <div className="relative animate-fade-in-up animate-delay-200 lg:col-span-5 mb-16 lg:mb-0">
              {/* Decorative blue card behind hero image (only visible on lg+) */}
              <div className="hidden lg:block absolute -top-4 -right-4 w-full h-full bg-[#1D4ED8] rounded-lg" aria-hidden="true"></div>
              <div className="relative rounded-lg overflow-hidden shadow-2xl">
                <img
                  src={hero.image_url ? (hero.image_url.startsWith('http') ? hero.image_url : `${API_URL}${hero.image_url}`) : HERO_IMAGE}
                  alt="Business Strategy Session"
                  className="w-full h-[360px] sm:h-[400px] lg:h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1C]/40 to-transparent"></div>
              </div>
              {/* Floating Card - shifted further so it doesn't crowd CTAs on mobile */}
              <div className="absolute -bottom-10 left-3 sm:-left-6 bg-white p-5 sm:p-6 shadow-xl border border-[#E2E8F0] rounded-md max-w-[260px] sm:max-w-[280px]">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-[#EFF6FF] rounded-full flex items-center justify-center">
                    <TrendingUp size={20} className="text-[#1D4ED8]" />
                  </div>
                  <span className="font-bold text-[#0A0F1C]">7-Stage Framework</span>
                </div>
                <p className="text-sm text-[#475569]">From idea to launch day with a proven system</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="trust-strip py-8" data-testid="trust-strip">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {trustStats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`trust-stat-${index}`}>
                <div className="heading-2 text-3xl lg:text-4xl text-[#1D4ED8] mb-1">{stat.value}</div>
                <div className="text-sm text-[#475569] font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured In / As Seen In - CMS-controlled, hidden until enabled */}
      {featuredIn.enabled && Array.isArray(featuredIn.logos) && featuredIn.logos.length > 0 && (
        <section className="bg-white border-b border-[#E2E8F0] py-8 lg:py-10" data-testid="featured-in-section">
          <div className="container-custom">
            <div className="text-center mb-6">
              <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#1D4ED8] mb-2" data-testid="featured-in-overline">{featuredIn.overline || 'Featured In / As Seen In'}</div>
              {featuredIn.headline && (
                <h3 className="text-lg lg:text-xl font-bold text-[#0A0F1C]" data-testid="featured-in-headline">{featuredIn.headline}</h3>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-8 lg:gap-12">
              {featuredIn.logos.map((logo, i) => {
                const src = logo.logo_url
                  ? (logo.logo_url.startsWith('http') ? logo.logo_url : `${API_URL}${logo.logo_url}`)
                  : null;
                const inner = src ? (
                  <img
                    src={src}
                    alt={logo.label || `Featured logo ${i + 1}`}
                    className="h-10 lg:h-12 w-auto object-contain grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all"
                    loading="lazy"
                  />
                ) : (
                  <span className="text-base lg:text-lg font-bold text-[#475569]/70 hover:text-[#0A0F1C] transition-colors">
                    {logo.label || 'Featured'}
                  </span>
                );
                return logo.link ? (
                  <a key={i} href={logo.link} target="_blank" rel="noopener noreferrer" data-testid={`featured-logo-${i}`}>{inner}</a>
                ) : (
                  <div key={i} data-testid={`featured-logo-${i}`}>{inner}</div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Inline Hero Lead Magnet Strip - 7-Stage Business Jumpstart Blueprint */}
      <HeroLeadMagnetStrip />

      {/* Why I Do This - Homepage Story Preview (asymmetric, offset overlap) */}
      <section className="section-padding bg-white relative overflow-hidden" data-testid="why-section">
        <div className="glow-blob orange hidden lg:block" style={{ top: '40px', left: '-220px' }} aria-hidden="true"></div>
        <div className="container-custom relative">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7 lg:pt-6">
              <div className="offset-tag mb-4" data-testid="why-overline">Why I Do This</div>
              <h2 className="heading-2 text-3xl lg:text-4xl text-[#0A0F1C] mb-5 asym-heading" data-testid="why-title">
                I Know What It Feels Like to Build While Working a 9-to-5
              </h2>
              <p className="body-text text-[#475569] mb-4 leading-relaxed">
                I know what it feels like to carry a business idea while still showing up to work, family, ministry, and everyday responsibilities. The vision is real, but the time is limited. The pressure is real, but so is the purpose.
              </p>
              <p className="body-text text-[#475569] mb-4 leading-relaxed">
                For years, I had to build in the margins. After work, late at night, between responsibilities, and sometimes with more vision than resources. That is why I do not just teach theory. I help entrepreneurs create structure, take the next right step, and build with discipline.
              </p>
              <p className="body-text text-[#475569] mb-6 leading-relaxed">
                If your business idea will not leave you alone, you do not need another random idea. You need a plan, a process, and somebody who understands what it means to build while life is still happening.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/about"
                  className="btn-outline inline-flex items-center justify-center gap-2"
                  data-testid="why-read-story-btn"
                >
                  Read My Story
                  <ArrowRight size={16} />
                </Link>
                <Link
                  to="/free-jumpstart-session"
                  className="btn-primary-blue inline-flex items-center justify-center gap-2"
                  data-testid="why-book-session-btn"
                >
                  Book a Free Session
                </Link>
              </div>
            </div>
            {/* Authority card offset upward and bordered by orange + overlapping stat callout */}
            <div className="lg:col-span-5 relative mt-8 lg:mt-0 lg:pt-6">
              <div className="bg-[#F8FAFC] border-l-4 border-[#F97316] p-6 rounded-r-lg shadow-md relative z-[1]" data-testid="why-authority-card">
                <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-[#F97316] mb-2">Authority That Comes From Doing</div>
                <p className="text-[#0A0F1C] font-bold text-lg leading-snug mb-3">
                  I don't just teach business strategy. I live it through real ventures, real systems, and real execution.
                </p>
                <ul className="space-y-2 text-sm text-[#475569]">
                  <li className="flex items-start gap-2"><CheckCircle size={16} className="text-[#1D4ED8] flex-shrink-0 mt-0.5" /> Built and operate 10+ active businesses</li>
                  <li className="flex items-start gap-2"><CheckCircle size={16} className="text-[#1D4ED8] flex-shrink-0 mt-0.5" /> Coached 100+ entrepreneurs from idea to launch</li>
                  <li className="flex items-start gap-2"><CheckCircle size={16} className="text-[#1D4ED8] flex-shrink-0 mt-0.5" /> 7-stage framework tested across industries</li>
                  <li className="flex items-start gap-2"><CheckCircle size={16} className="text-[#1D4ED8] flex-shrink-0 mt-0.5" /> 15+ years building while serving and leading</li>
                </ul>
              </div>
              {/* Overlapping stat callout - positioned BELOW so it does not collide with the card content */}
              <div className="hidden sm:flex float-stat mt-6 lg:mt-8 ml-0 lg:-ml-6" data-testid="why-stat-callout">
                <span className="text-[10px] font-bold tracking-[0.14em] uppercase text-[#F97316]">Tested across</span>
                <span className="text-2xl font-extrabold text-[#0A0F1C] leading-none mt-1">9 industries</span>
                <span className="text-xs text-[#475569] mt-1">Ministry, health, tech, produce, finance, education and more</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hidden Video Section Placeholder - shows when video_enabled flag is true via admin/CMS */}
      {videoSection.enabled && (
        <section className="section-padding bg-[#FAFAFA]" data-testid="welcome-video-section">
          <div className="container-custom text-center">
            <span className="overline mb-3 inline-block">A Personal Message from David</span>
            <h2 className="heading-2 text-3xl lg:text-4xl text-[#0A0F1C] mb-3">
              {videoSection.heading || 'Why structure, strategy, and accountability matter when building your business'}
            </h2>
            {videoSection.sub && <p className="text-[#475569] max-w-2xl mx-auto mb-6">{videoSection.sub}</p>}
            {videoSection.video_url ? (
              <div className="aspect-video max-w-3xl mx-auto mt-6 rounded-lg overflow-hidden bg-[#0F172A]">
                <iframe src={videoSection.video_url} title="A Personal Message from David" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
            ) : (
              <div className="aspect-video bg-[#0F172A] rounded-lg max-w-3xl mx-auto mt-6 flex items-center justify-center text-[#94A3B8] text-sm">Add a video URL in Site Manager &rarr; Content &rarr; Video Section</div>
            )}
          </div>
        </section>
      )}

      {/* Services Preview - asymmetric headline + description split */}
      <section className="section-padding bg-[#FAFAFA] relative" data-testid="services-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-12 gap-8 items-end mb-8">
            <div className="lg:col-span-7">
              <span className="overline mb-3 inline-block">Services</span>
              <h2 className="heading-2 text-3xl lg:text-4xl text-[#0A0F1C] asym-heading">
                How I Can Help You
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="body-text text-[#475569]">
                Strategic guidance and practical support to help you create additional income streams, on your timeline, with your constraints, in your industry.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const Icon = getServiceIcon(service.icon);
              return (
                <Link 
                  key={service.service_id || index}
                  to={`/services/${service.slug}`}
                  className="card-marketing group"
                  data-testid={`service-card-${index}`}
                >
                  <div className="w-12 h-12 bg-[#EFF6FF] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#1D4ED8] transition-colors">
                    <Icon size={24} className="text-[#1D4ED8] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="heading-3 text-lg text-[#0A0F1C] mb-2">{service.title}</h3>
                  <p className="text-sm text-[#475569] mb-4">{service.short_description}</p>
                  <span className="inline-flex items-center gap-1 text-[#1D4ED8] text-sm font-bold group-hover:gap-2 transition-all">
                    Learn More <ArrowRight size={14} />
                  </span>
                </Link>
              );
            })}
          </div>
          
          <div className="text-center mt-10">
            <Link 
              to="/services" 
              className="btn-outline inline-flex items-center gap-2"
              data-testid="view-all-services-btn"
            >
              View All Services
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Business Jumpstart Framework */}
      <section className="section-padding bg-white relative overflow-hidden" data-testid="framework-section">
        <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none" aria-hidden="true"></div>
        <div className="container-custom relative">
          <div className="grid lg:grid-cols-12 gap-6 items-end mb-8">
            <div className="lg:col-span-8">
              <span className="overline mb-3 inline-block">The Process</span>
              <h2 className="heading-2 text-3xl lg:text-4xl text-[#0A0F1C] asym-heading">
                Business Jumpstart Framework
              </h2>
            </div>
            <div className="lg:col-span-4">
              <p className="body-text text-[#475569]">
                A proven 7-stage system to take you from business idea to launch day and beyond.
              </p>
            </div>
          </div>
          
          <div className="framework-grid bg-white relative">
            {frameworkStages.map((stage, index) => {
              const Icon = stage.icon;
              return (
                <div 
                  key={index} 
                  className="framework-item"
                  data-testid={`framework-stage-${index}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-[#F97316] font-bold text-sm">{stage.number}</span>
                    <Icon size={20} className="text-[#1D4ED8]" />
                  </div>
                  <h3 className="heading-3 text-lg text-[#0A0F1C] mb-2">{stage.name}</h3>
                  <p className="text-sm text-[#475569]">{stage.description}</p>
                </div>
              );
            })}
            {/* CTA Cell */}
            <div className="framework-item bg-[#1D4ED8] flex flex-col justify-center items-center text-center">
              <p className="text-white font-bold mb-4">Ready to jumpstart your business?</p>
              <Link
                to="/free-jumpstart-session"
                className="bg-[#F97316] text-white px-6 py-3 font-bold text-sm hover:bg-[#FB923C] transition-colors"
                data-testid="framework-cta"
              >
                Book Free Session
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Free Resources - asymmetric with offset image, image-left layout flip */}
      <section className="section-padding bg-[#FAFAFA] relative overflow-hidden" data-testid="resources-section">
        <div className="glow-blob blue hidden lg:block" style={{ bottom: '-200px', right: '-180px' }} aria-hidden="true"></div>
        <div className="container-custom relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            {/* Image first on lg for layout-flip rhythm */}
            <div className="relative order-2 lg:order-1 lg:col-span-5">
              <div className="hidden lg:block absolute -bottom-4 -left-4 w-full h-full bg-[#F97316] rounded-lg" aria-hidden="true"></div>
              <img
                src="https://customer-assets.emergentagent.com/job_d8aebd02-1a2f-47f4-a1b5-34ff2bb8d378/artifacts/p10flbi8_IMG-5843.JPG"
                alt="David C. Ashley"
                className="relative rounded-lg shadow-xl w-full object-cover"
              />
              {/* Small overlapping callout */}
              <div className="hidden sm:block absolute -top-5 -right-5 bg-white border border-[#E2E8F0] shadow-lg rounded-md px-4 py-3" data-testid="resources-stat-callout">
                <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-[#1D4ED8]">Free Downloads</div>
                <div className="text-xl font-extrabold text-[#0A0F1C] leading-none mt-1">{resources.length || 2}+ Checklists</div>
              </div>
            </div>
            <div className="order-1 lg:order-2 lg:col-span-7">
              <span className="overline mb-3 inline-block">Free Resources</span>
              <h2 className="heading-2 text-3xl lg:text-4xl text-[#0A0F1C] asym-heading mb-5">
                Start Your Journey Today
              </h2>
              <p className="body-text text-[#475569] mb-5">
                Download our free checklists to begin your business journey with clarity and confidence.
              </p>

              <div className="space-y-3">
                {resources.map((resource, index) => (
                  <Link
                    key={resource.resource_id || index}
                    to={`/resources/${resource.slug}`}
                    className="flex items-start gap-4 p-4 bg-white border border-[#E2E8F0] rounded-lg hover:border-[#1D4ED8] hover:shadow-md transition-all group"
                    data-testid={`resource-card-${index}`}
                  >
                    <div className="w-12 h-12 bg-[#FFF7ED] rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileCheck size={24} className="text-[#F97316]" />
                    </div>
                    <div className="flex-grow">
                      <h3 className="font-bold text-[#0A0F1C] mb-1 group-hover:text-[#1D4ED8] transition-colors">
                        {resource.title}
                      </h3>
                      <p className="text-sm text-[#475569]">{resource.short_description}</p>
                    </div>
                    <ArrowRight size={20} className="text-[#94A3B8] group-hover:text-[#1D4ED8] transition-colors flex-shrink-0 mt-1" />
                  </Link>
                ))}
              </div>

              <Link
                to="/resources"
                className="inline-flex items-center gap-2 text-[#1D4ED8] font-bold mt-6 hover:gap-3 transition-all"
                data-testid="view-all-resources-link"
              >
                View All Resources <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Preview - dark section with ambient dot grid */}
      <section className="section-padding bg-[#0F172A] text-white relative overflow-hidden" data-testid="about-section">
        <div className="absolute inset-0 dot-grid-dark opacity-60 pointer-events-none" aria-hidden="true"></div>
        <div className="container-custom relative">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#1E293B] p-6 rounded-lg">
                  <Building size={32} className="text-[#1D4ED8] mb-3" />
                  <div className="text-2xl font-bold text-white mb-1">10+</div>
                  <div className="text-sm text-[#94A3B8]">Active Businesses</div>
                </div>
                <div className="bg-[#1E293B] p-6 rounded-lg">
                  <Award size={32} className="text-[#F97316] mb-3" />
                  <div className="text-2xl font-bold text-white mb-1">15+</div>
                  <div className="text-sm text-[#94A3B8]">Years Experience</div>
                </div>
                <div className="bg-[#1E293B] p-6 rounded-lg col-span-2">
                  <Users size={32} className="text-[#1D4ED8] mb-3" />
                  <div className="text-2xl font-bold text-white mb-1">100+</div>
                  <div className="text-sm text-[#94A3B8]">Entrepreneurs Helped</div>
                </div>
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <span className="text-[#F97316] font-bold text-sm uppercase tracking-wider mb-3 inline-block">About David Ashley</span>
              <h2 className="heading-2 text-3xl lg:text-4xl text-white mb-6">
                I Don't Just Teach Strategy. I Live It.
              </h2>
              <p className="text-[#94A3B8] mb-6 leading-relaxed">
                As a business strategist, I've built and operate multiple successful businesses across various
                industries, from health and wellness to technology, from ministry to produce. I understand the
                challenges you face because I've faced them myself.
              </p>
              <p className="text-[#94A3B8] mb-8 leading-relaxed">
                My diverse portfolio isn't just proof of concept. It's the foundation of the practical,
                real-world strategies I share with you.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/about" 
                  className="btn-primary-blue inline-flex items-center justify-center gap-2"
                  data-testid="about-learn-more-btn"
                >
                  Learn More About Me
                  <ArrowRight size={18} />
                </Link>
                <Link 
                  to="/brands" 
                  className="btn-outline border-white text-white hover:bg-white/10 inline-flex items-center justify-center gap-2"
                  data-testid="about-see-brands-btn"
                >
                  See My Brands
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Latest from YouTube */}
      {ytData && (
        <section className="section-padding bg-[#0B0B0F] text-white relative overflow-hidden" data-testid="youtube-section">
          <div className="container-custom relative">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
              <div>
                <div className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.14em] uppercase text-[#F87171] mb-3">
                  <Youtube size={16} /> Latest from YouTube
                </div>
                <h2 className="font-bold text-3xl lg:text-4xl leading-tight">Strategy, straight from the source</h2>
                <p className="text-[#A1A1AA] text-sm lg:text-base mt-2 max-w-xl">
                  {ytData.channel_title ? `New videos from ${ytData.channel_title}.` : 'Fresh business strategy videos and insights.'} Watch, learn, and put it to work.
                </p>
              </div>
              <a
                href={ytData.channel_url || 'https://www.youtube.com/@DavidAshley-BusinessStrategist'}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="youtube-channel-cta"
                className="inline-flex items-center gap-2 bg-[#FF0000] hover:bg-[#E00000] text-white font-bold text-sm px-5 py-3 rounded-full transition-colors whitespace-nowrap self-start sm:self-auto"
              >
                <Youtube size={18} /> Visit the channel
              </a>
            </div>

            {(ytData.videos && ytData.videos.length > 0) ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="youtube-grid">
                {ytData.videos.map((v) => (
                  <a
                    key={v.video_id}
                    href={v.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`youtube-video-${v.video_id}`}
                    className="group block rounded-2xl overflow-hidden bg-[#18181B] border border-white/5 hover:border-white/20 transition-colors"
                  >
                    <div className="relative aspect-video overflow-hidden bg-black">
                      <img
                        src={v.thumbnail}
                        alt={v.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
                        <span className="w-14 h-14 rounded-full bg-[#FF0000] flex items-center justify-center shadow-lg">
                          <Play size={22} className="text-white ml-0.5" fill="white" />
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-sm leading-snug line-clamp-2 group-hover:text-[#F87171] transition-colors">{v.title}</h3>
                      <div className="flex items-center gap-2 mt-2 text-[11px] text-[#A1A1AA]">
                        {v.published && <span>{new Date(v.published).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                        {typeof v.views === 'number' && v.views > 0 && <span>&bull; {v.views.toLocaleString()} views</span>}
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            ) : (
              <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-[#1a0000] via-[#18181B] to-[#0B0B0F] overflow-hidden" data-testid="youtube-empty">
                {/* glow accents */}
                <div className="pointer-events-none absolute -top-24 -right-16 w-72 h-72 rounded-full bg-[#FF0000]/20 blur-3xl" aria-hidden="true" />
                <div className="pointer-events-none absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-fuchsia-600/10 blur-3xl" aria-hidden="true" />
                <div className="relative grid lg:grid-cols-2 gap-8 p-8 lg:p-12 items-center">
                  {/* Left: message + CTA */}
                  <div>
                    <span className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.16em] text-[#F87171] bg-white/5 border border-white/10 rounded-full px-3 py-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#FF0000] animate-pulse" /> Channel launching soon
                    </span>
                    <h3 className="font-bold text-2xl lg:text-3xl mt-4 leading-tight">Fresh business strategy videos are on the way</h3>
                    <p className="text-[#A1A1AA] text-sm lg:text-base mt-3 max-w-md">Subscribe now and you'll be the first to watch — practical playbooks on growth, funding, and building something that lasts.</p>
                    <div className="flex flex-wrap items-center gap-3 mt-6">
                      <a
                        href={ytData.channel_url || 'https://www.youtube.com/@DavidAshley-BusinessStrategist'}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="youtube-subscribe-cta"
                        className="inline-flex items-center gap-2 bg-[#FF0000] hover:bg-[#E00000] text-white font-bold text-sm px-6 py-3.5 rounded-full transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-red-900/40"
                      >
                        <Youtube size={20} /> Subscribe on YouTube
                      </a>
                      <a
                        href={ytData.channel_url || 'https://www.youtube.com/@DavidAshley-BusinessStrategist'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-white/80 hover:text-white font-semibold text-sm px-4 py-3.5 rounded-full border border-white/15 hover:border-white/30 transition-colors"
                      >
                        Visit channel <ExternalLink size={15} />
                      </a>
                    </div>
                  </div>
                  {/* Right: decorative "upcoming video" tiles */}
                  <div className="grid grid-cols-2 gap-4" aria-hidden="true">
                    {[
                      { label: 'Business Strategy', cls: 'from-[#FF0000]/30 to-[#7C1D1D]/20', big: true },
                      { label: 'Funding & Growth', cls: 'from-fuchsia-600/25 to-indigo-700/20' },
                      { label: 'Founder Mindset', cls: 'from-blue-600/25 to-cyan-600/15' },
                    ].map((t, i) => (
                      <div key={t.label} className={`group relative rounded-2xl border border-white/10 bg-gradient-to-br ${t.cls} overflow-hidden ${t.big ? 'col-span-2 aspect-[16/7]' : 'aspect-video'}`}>
                        <div className="absolute inset-0 bg-[linear-gradient(110deg,transparent_30%,rgba(255,255,255,0.08)_50%,transparent_70%)] bg-[length:200%_100%] animate-[shimmer_2.5s_infinite]" style={{ animationDelay: `${i * 0.4}s` }} />
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                          <span className="w-12 h-12 rounded-full bg-[#FF0000] flex items-center justify-center shadow-lg shadow-red-900/40">
                            <Play size={18} className="text-white ml-0.5" fill="white" />
                          </span>
                        </div>
                        <span className="absolute bottom-2.5 left-3 text-[11px] font-bold text-white/85">{t.label}</span>
                        <span className="absolute bottom-2.5 right-3 text-[10px] font-semibold text-white/50">Coming soon</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      )}


      {/* Brand Ecosystem Preview - asymmetric heading */}
      <section className="section-padding bg-white relative" data-testid="brands-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-12 gap-6 items-end mb-8">
            <div className="lg:col-span-7">
              <span className="overline mb-3 inline-block">Brand Ecosystem</span>
              <h2 className="heading-2 text-3xl lg:text-4xl text-[#0A0F1C] asym-heading">
                A Portfolio of Purpose-Driven Ventures
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="body-text text-[#475569]">
                From ministry to business, each venture represents a commitment to impact and excellence.
              </p>
            </div>
          </div>
          
          <div className="bento-grid">
            {brands.map((brand, index) => (
              <div 
                key={brand.brand_id || index}
                className={`card-marketing group relative ${index === 0 ? 'lg:col-span-2 lg:row-span-2' : ''}`}
                data-testid={`brand-card-${index}`}
              >
                <span className={`badge-blue mb-3 inline-block ${getCategoryColor(brand.category)}`}>
                  {brand.category}
                </span>
                <h3 className={`heading-3 ${index === 0 ? 'text-xl' : 'text-lg'} text-[#0A0F1C] mb-2`}>
                  {brand.name}
                </h3>
                <p className="text-sm text-[#475569] mb-4">{brand.description}</p>
                {brand.website_url ? (
                  <a 
                    href={brand.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[#1D4ED8] text-sm font-bold hover:gap-2 transition-all"
                    data-testid={`brand-link-${index}`}
                  >
                    Visit Website <ExternalLink size={14} />
                  </a>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[#94A3B8] text-sm font-medium">
                    Coming Soon
                  </span>
                )}
              </div>
            ))}
          </div>
          
          <div className="text-center mt-10">
            <Link 
              to="/brands" 
              className="btn-primary-blue inline-flex items-center gap-2"
              data-testid="view-all-brands-btn"
            >
              View Complete Portfolio
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Meet Our Coaches - lead-gen funnel into the coach roster */}
      {coaches.length > 0 && (
        <section className="section-padding bg-white relative overflow-hidden" data-testid="coaches-section">
          <div className="container-custom relative">
            <div className="grid lg:grid-cols-12 gap-6 items-end mb-8">
              <div className="lg:col-span-7">
                <span className="overline mb-3 inline-block">Work 1-on-1</span>
                <h2 className="heading-2 text-3xl lg:text-4xl text-[#0A0F1C] asym-heading">
                  Meet Our Business Coaches
                </h2>
              </div>
              <div className="lg:col-span-5">
                <p className="body-text text-[#475569]">
                  Get matched with a vetted DCA coach who has walked the path you're on. Personalized strategy, real accountability, measurable results.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {coaches.map((coach, index) => (
                <Link
                  key={coach.coach_id || index}
                  to={`/coaches/${coach.slug}`}
                  className="card-marketing group flex flex-col"
                  data-testid={`home-coach-card-${index}`}
                >
                  <div className="flex items-center gap-4 mb-4">
                    {coach.photo_url ? (
                      <img src={coach.photo_url} alt={coach.name} className="w-16 h-16 rounded-full object-cover border-2 border-[#EFF6FF]" />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-[#1D4ED8] flex items-center justify-center text-white font-bold text-xl">
                        {coach.name?.charAt(0) || 'C'}
                      </div>
                    )}
                    <div className="min-w-0">
                      <h3 className="font-bold text-[#0A0F1C] truncate group-hover:text-[#1D4ED8] transition-colors">{coach.name}</h3>
                      {coach.avg_rating != null ? (
                        <div className="flex items-center gap-1 text-sm text-[#475569]">
                          <Star size={14} className="text-[#F97316] fill-[#F97316]" />
                          <span className="font-bold text-[#0A0F1C]">{coach.avg_rating}</span>
                          <span className="text-[#94A3B8]">({coach.review_count})</span>
                        </div>
                      ) : (
                        <span className="text-xs text-[#94A3B8]">New coach</span>
                      )}
                    </div>
                  </div>
                  {coach.headline && <p className="text-sm font-semibold text-[#0A0F1C] mb-1">{coach.headline}</p>}
                  {coach.bio && <p className="text-sm text-[#475569] line-clamp-3 mb-4">{coach.bio}</p>}
                  {Array.isArray(coach.expertise) && coach.expertise.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {coach.expertise.slice(0, 3).map((e, i) => (
                        <span key={i} className="text-[11px] bg-[#EFF6FF] text-[#1D4ED8] px-2 py-0.5 rounded-full font-medium">{e}</span>
                      ))}
                    </div>
                  )}
                  <span className="inline-flex items-center gap-1 text-[#1D4ED8] text-sm font-bold mt-auto group-hover:gap-2 transition-all">
                    View Profile <ArrowRight size={14} />
                  </span>
                </Link>
              ))}
            </div>

            <div className="text-center mt-10">
              <Link
                to="/coaches"
                className="btn-primary-orange inline-flex items-center gap-2"
                data-testid="find-a-coach-btn"
              >
                Find a Coach
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Testimonials - centered (intentional, marquee) but with subtle ambient backdrop */}
      <section className="section-padding bg-[#FAFAFA] relative overflow-hidden" data-testid="testimonials-section">
        <div className="glow-blob orange hidden lg:block" style={{ top: '-160px', right: '-200px' }} aria-hidden="true"></div>
        <div className="container-custom relative">
          <div className="text-center mb-8">
            <span className="overline mb-3 inline-block">Sample Client Transformations</span>
            <h2 className="heading-2 text-3xl lg:text-4xl text-[#0A0F1C] mb-3">
              Sample Client Transformations
            </h2>
            <p className="body-text text-[#475569] max-w-2xl mx-auto">
              Examples of the types of business breakthroughs this framework is designed to help entrepreneurs create. Real client stories will be added as they are collected.
            </p>
          </div>

          <TestimonialCarousel testimonials={testimonials} bgGradientFrom="#FAFAFA" />
        </div>
      </section>

      {/* Social Studio pricing funnel */}
      <StudioPricingSection />

      {/* Final CTA with urgency */}
      <section className="section-padding bg-[#1D4ED8]" data-testid="final-cta-section">
        <div className="container-custom text-center">
          <div className="text-[#BFDBFE] text-xs font-bold tracking-[0.14em] uppercase mb-4" data-testid="final-cta-urgency">
            {finalCta.urgency_line || 'Stop sitting on your business idea'}
          </div>
          <h2 className="heading-2 text-3xl lg:text-4xl text-white mb-3">
            {finalCta.headline || 'Your Vision Needs Structure'}
          </h2>
          <p className="text-[#BFDBFE] text-lg mb-2 max-w-2xl mx-auto" dangerouslySetInnerHTML={{ __html: finalCta.body || "Most entrepreneurs don't fail because they lack ideas. They fail because they lack <strong class='text-white'>structure, systems, and execution</strong>." }} />
          <p className="text-[#BFDBFE] text-base mb-6 max-w-2xl mx-auto">
            {finalCta.sub_body || "The longer you wait, the longer your idea stays stuck. Let's build the plan together."}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to={finalCta.primary_cta_link || '/free-jumpstart-session'}
              className="btn-primary-orange inline-flex items-center justify-center gap-2"
              data-testid="final-cta-primary"
            >
              {finalCta.primary_cta_label || 'Book Your FREE 20-Minute Business Jumpstart Session'}
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/contact"
              className="bg-white/10 border border-white/30 text-white px-8 py-4 font-bold text-sm hover:bg-white/20 transition-colors inline-flex items-center justify-center gap-2"
              data-testid="final-cta-secondary"
            >
              Contact David
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
